"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, ChevronLeft, Download, CheckCircle, Smartphone } from "lucide-react";
import { EvaluationChat } from "./EvaluationChat";
import { scenarios } from "./scenarios";
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";

interface CompetenceInterviewGuideProps {
    token: string;
}

export function CompetenceInterviewGuide({ token }: CompetenceInterviewGuideProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [logs, setLogs] = useState<Record<string, { role: "user" | "assistant"; content: string }[]>>({});
    const [isFinished, setIsFinished] = useState(false);

    const handleLogUpdate = (scenarioId: string, role: "user" | "assistant", content: string) => {
        setLogs((prev) => {
            const scenarioLogs = prev[scenarioId] || [];
            return {
                ...prev,
                [scenarioId]: [...scenarioLogs, { role, content }],
            };
        });
    };

    const activeScenario = scenarios[currentStep];
    const progress = ((currentStep + 1) / scenarios.length) * 100;

    const nextStep = () => {
        if (currentStep < scenarios.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            setIsFinished(true);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleDownloadPDF = async () => {
        const doc = new jsPDF();

        // Title
        doc.setFontSize(20);
        doc.text("Agile Coach Competence Report", 14, 22);

        doc.setFontSize(10);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 35);


        // Process data for table
        // We want to group by Scenario
        const tableData: RowInput[] = [];

        scenarios.forEach(scenario => {
            const scenarioLogs = logs[scenario.id] || [];
            if (scenarioLogs.length > 0) {
                // Add header row for scenario
                tableData.push([{ content: scenario.title.toUpperCase(), colSpan: 2, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);

                scenarioLogs.forEach((log) => {
                    tableData.push([
                        log.role === 'user' ? 'Candidate' : 'AI Coach',
                        log.content
                    ]);
                });
            }
        });

        autoTable(doc, {
            head: [['Role', 'Message']],
            body: tableData,
            startY: 40,
            styles: { overflow: 'linebreak', cellWidth: 'wrap' },
            columnStyles: {
                0: { cellWidth: 30, fontStyle: 'bold' },
                1: { cellWidth: 'auto' }
            },
            theme: 'grid'
        });

        doc.save(`agile-coach-competence-${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (isFinished) {
        return (
            <div className="flex flex-col items-center justify-center h-full space-y-8 py-12">
                <div className="rounded-full bg-green-100 p-6 dark:bg-green-900/20">
                    <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-center space-y-2 max-w-lg">
                    <h2 className="text-3xl font-bold">Assessment Complete!</h2>
                    <p className="text-muted-foreground">
                        You have completed all scenarios. You can now download a PDF report of your responses and the AI feedback to review later or share.
                    </p>
                </div>

                <Button size="lg" onClick={handleDownloadPDF} className="gap-2">
                    <Download className="h-5 w-5" />
                    Download PDF Report
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <div className="flex justify-between items-end">
                    <div>
                        <h3 className="text-xl font-bold">Agile Coach Competence Test</h3>
                        <p className="text-muted-foreground text-sm">Scenario {currentStep + 1} of {scenarios.length}: {activeScenario.title}</p>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* LEFT COLUMN: Visuals */}
                <div className="space-y-6">
                    <div className="bg-muted/50 p-6 rounded-xl border border-border/50">
                        <h4 className="font-bold mb-2 text-lg">{activeScenario.title}</h4>
                        <p className="text-muted-foreground mb-6">{activeScenario.description}</p>

                        <div className="bg-card rounded-lg shadow-sm">
                            {activeScenario.component}
                        </div>
                    </div>

                    <div className="flex justify-between pt-4">
                        <Button
                            variant="ghost"
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className="gap-2"
                        >
                            <ChevronLeft className="h-4 w-4" /> Previous
                        </Button>
                        <Button
                            onClick={nextStep}
                            className="gap-2"
                        >
                            {currentStep === scenarios.length - 1 ? "Finish Assessment" : "Next Scenario"}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* RIGHT COLUMN: Interactive Chat */}
                <div className="h-full">
                    {/* We key the chat by scenario ID so it resets/reloads for each step */}
                    <EvaluationChat
                        key={activeScenario.id}
                        scenarioId={activeScenario.id}
                        initialMessage={activeScenario.initialAiMessage}
                        context={activeScenario.context}
                        idealAnswer={activeScenario.idealAnswer}
                        token={token}
                        onLog={(role, content) => handleLogUpdate(activeScenario.id, role, content)}
                    />
                </div>
            </div>
        </div>
    );
}
